<template>
  <div id="app" :style="{ 'padding-bottom': visibleToolButtons ? '6.5rem' : '2.25rem' }">
    <b-navbar class="text-nowrap" type="dark" variant="primary" fixed="top" toggleable="true">
      <b-nav-text v-if="visibleBackButton" class="py-0">
        <b-button class="rounded-circle p-0" variant="primary" @click="onNavigateBack()">
          <i class="bi bi-chevron-left m-1"></i>
        </b-button>
      </b-nav-text>
      <b-nav-text class="text-white">{{ title }}</b-nav-text>
      <b-nav-text class="text-white ml-auto">{{ userName }}</b-nav-text>
      <b-navbar-toggle
        v-b-toggle.app-sidebar
        class="border border-0 px-0 ml-3"
        target="navbar-toggle-collapse"
      ></b-navbar-toggle>
    </b-navbar>
    <b-sidebar
      id="app-sidebar"
      backdrop
      right
      shadow="true"
      bg-variant="primary"
      text-variant="light"
      width="13rem"
    >
      <b-list-group>
        <template v-if="$store.getters.loggedIn">
          <template v-for="func in $store.getters.term.funcs">
            <b-list-group-item
              v-if="func.useMenu"
              :active="false"
              :disabled="func.formId === viewId"
              :key="func.formId"
              href="#"
              :class="[
                'bg-primary',
                ' rounded-0',
                func.formId === viewId ? ' text-secondary' : ' text-white'
              ]"
              @click="onNavigatePage(func.formId)"
              button
              >{{ func.funcNm }}</b-list-group-item
            >
          </template>
        </template>
        <b-list-group-item
          v-else-if="!$store.getters.loggedIn"
          :active="false"
          :disabled="viewId === 'UserLogin'"
          href="#"
          :class="[
            'bg-primary',
            'rounded-0',
            viewId === 'UserLogin' ? ' text-secondary' : ' text-white'
          ]"
          button
          @click="onNavigatePage('UserLogin')"
          >{{ this.codeTypes ? 'ログイン' : '出力履歴' }}</b-list-group-item
        >
        <b-list-group-item
          :active="false"
          :disabled="viewId === 'LicenseInfo'"
          href="#"
          :class="[
            'bg-primary',
            'rounded-0',
            viewId === 'LicenseInfo' ? 'text-secondary' : 'text-white'
          ]"
          button
          @click="onNavigatePage('LicenseInfo')"
          >ライセンス情報</b-list-group-item
        >
        <b-list-group-item
          :active="false"
          :disabled="viewId === 'VersionInfo'"
          href="#"
          :class="[
            'bg-primary',
            'rounded-0',
            viewId === 'VersionInfo' ? ' text-secondary' : ' text-white'
          ]"
          button
          @click="onNavigatePage('VersionInfo')"
          >バージョン情報</b-list-group-item
        >
        <template v-if="$store.getters.loggedIn">
          <b-list-group-item
            :active="false"
            class="bg-primary rounded-0 text-white"
            button
            @click="logout()"
            >ログアウト</b-list-group-item
          >
        </template>
      </b-list-group>
    </b-sidebar>
    <router-view
      ref="child"
      @initView="onInitView"
      @enabledScan="onEnabledScan"
      @updateCodeTypes="onUpdateCodeTypes"
    ></router-view>
    <div v-if="visibleToolButtons" class="d-flex justify-content-between mx-3 mb-3">
      <div>
        <b-button variant="primary" @click="onClickClearInput">
          <i class="bi bi-arrow-counterclockwise h2"></i>
        </b-button>
      </div>
      <div v-show="visibleScanButton">
        <b-button
          variant="primary"
          :disabled="!enabledScan"
          @click="onClickScan"
          v-b-modal.modal-scan
        >
          <i class="bi bi-record-circle h2"></i>
        </b-button>
      </div>
      <div>
        <b-form-radio-group
          v-model="selectedInputMode"
          :options="inputModes"
          class="ml-auto"
          buttons
          button-variant="outline-primary"
          :disabled="!enabledScan"
          @change="onChangeInputMode"
        ></b-form-radio-group>
      </div>
    </div>
    <b-alert class="p-1 mb-0 rounded-0" show variant="primary">
      <span class="h5">{{ message }}</span>
    </b-alert>
    <b-row v-show="false">
      <b-col>
        <b-form-input v-model="scanResult" @change="onChangeScan"></b-form-input>
      </b-col>
      <b-col>
        <b-form-input v-model="scanCodeType"></b-form-input>
      </b-col>
      <b-col>
        <b-form-input v-model="scanData"></b-form-input>
      </b-col>
    </b-row>
    <b-modal
      id="modal-scan"
      centered
      hide-footer
      title="スキャン"
      @show="onShowScanModal"
      @shown="onShownScanModal"
      @hidden="onHiddenScanModal"
    >
      <video v-show="isReadonlyBarcode" id="scanVideo" width="300" height="400"></video>
      <b-input-group class="mt-2 py-0" size="lg">
        <b-form-input
          ref="modalData"
          v-model="barcodeBuff"
          :readonly="isReadonlyBarcode"
        ></b-form-input>
        <b-input-group-append>
          <b-button variant="secondary" @click="onRetryScan">
            <span class="bi bi-arrow-counterclockwise"></span>
          </b-button>
        </b-input-group-append>
      </b-input-group>
      <b-row class="mt-2">
        <b-col>
          <b-button variant="primary" @click="onDecideScan" block>決定</b-button>
        </b-col>
      </b-row>
    </b-modal>
  </div>
</template>

<script>
import { getDateValue, replaceConvertValue } from '@/compare';
import { substring_sjis, length_sjis } from '@/CommonFunctions';
import {
  isDXTerminal,
  scannerSetUseBarcode,
  scannerSetUseOcr,
  scannerSetClear,
  scannerSetReadCallback,
  scannerClearReadCallback,
  isAndroid
} from '@/lib/kjs-wrapper';
import {
  str,
  inputMode,
  codeType,
  scanResult,
  allowDup,
  dataLayout,
  dataSplit,
  columnType
} from '@/constants';

export default {
  data() {
    return {
      viewId: null, // 画面ID
      barcodeBuff: '', // キーボード入力バーコードのバッファ
      title: null, // タイトル
      inputModes: [], // 入力モード配列
      selectedInputMode: null, // 選択した入力モード
      useToolButtons: true, // ツールボタン使用機能か
      nowPriorityNo: 0, // 現在のバーコード優先順位
      nextBarcodeId: null, // 次のバーコードID
      visibleScan: false, // Scan表示
      codeTypes: null, // コード種配列
      stream: null,
      codeType: null, // 選択コード種
      isReadonlyBarcode: false, // 読取専用バーコード
      headerValue: {},
      detailValues: [],
      scanResult: '',
      scanCodeType: '',
      scanData: '',
      enabledScan: false //
    };
  },
  async mounted() {
    if (isDXTerminal()) {
      // Keyenceライブラリにコールバック関数を紐づけ
      scannerSetReadCallback('acceptScanResult');
    } else if (isAndroid()) {
      // Android端末を認識した場合はスキャン表示
      this.visibleScan = true;
    } else {
      // DX端末出ない場合はバーコード入力を開始
      document.addEventListener('keydown', this.onKeyDown);
    }
  },
  async beforeDestroy() {
    if (isDXTerminal()) {
      // Keyenceライブラリからコールバック関数の紐づけ解除
      scannerClearReadCallback();
    } else if (isAndroid()) {
      // Android端末を認識した場合はなにもしない
    } else {
      // DX端末でない場合はバーコード入力を停止
      document.removeEventListener('keydown', this.onKeyDown);
    }
  },
  computed: {
    visibleBackButton: function () {
      return this.$store.getters.historyCount > 0;
    },
    visibleMenu: function () {
      return this.$store.getters.loggedIn;
    },
    visibleToolButtons: function () {
      return this.useToolButtons;
    },
    visibleScanButton: function () {
      return isAndroid() && this.selectedInputMode == inputMode.BARCODE;
    },
    userName: function () {
      if (this.$store.getters.loggedIn) return this.$store.getters.userName;
      else return this.$store.getters.term ? this.$store.getters.term.terminalName : str.SPACE_FULL;
    },
    message: function () {
      return this.$store.getters.message ? this.$store.getters.message : str.SPACE_FULL;
    }
  },
  methods: {
    /**
     * キー入力イベント(DX端末以外バーコード入力)
     * @param {!object} event イベント引数{key,code}
     */
    onKeyDown: function (event) {
      // 入力モードがバーコードでない場合は処理しない
      if (this.selectedInputMode !== inputMode.BARCODE) return;

      const element = document.activeElement;
      if ((element.type === 'text' || element.type === 'password') && !element.disabled) {
        this.barcodeBuff = '';
        return;
      }

      if (event.key.length === 1) {
        if (event.key.match(/[0-9]/g)) {
          this.barcodeBuff += event.key;
        } else if (event.key.match(/[a-z]/g)) {
          this.barcodeBuff += event.key;
        } else if (event.key.match(/[A-Z]/g)) {
          this.barcodeBuff += event.key;
        } else if ('!"#$%&\'()-=^~\\|@`[{;+:*]},<.>/?_'.indexOf(event.key) !== -1) {
          this.barcodeBuff += event.key;
        }
      } else if (event.key === 'Enter') {
        // 空送信は認めない
        if (this.barcodeBuff === '') return;

        // バーコード読取成功を送信する
        this.acceptScanResult({
          mCodeType: codeType.KEYBOARD,
          mStringData: this.barcodeBuff,
          mDecodeResult: scanResult.SUCCESS
        });
        this.barcodeBuff = '';
      }
    },
    /**
     * クリアボタン押下
     */
    onClickClearInput: function () {
      this.nowPriorityNo = 0;

      // 子コンポーネントの入力クリア処理呼出
      this.$refs.child.clearInput();
    },
    /**
     * スキャン表示ボタン押下
     */
    onClickScan: function () {
      this.$bvModal.show('modal-scan');
    },
    /**
     * スキャンモーダル表示
     */
    onShowScanModal: async function () {
      // 初回バーコード読込の場合はバーコードの優先順位を初期化する
      if (this.nowPriorityNo === 0) {
        console.log('現在優先順位リセット');
        this.nextBarcodeId = null;
        this.headerValue = {};
        this.detailValues = [];
      }

      // 現在処理中のバーコードを取得
      let currentBarcode = null;
      if (this.codeTypes) {
        const read = this.$store.getters.currentRead;
        const rdetail = read.details.find((rdetail) => rdetail.priorityNo > this.nowPriorityNo);
        let barcodeId = this.nextBarcodeId ? this.nextBarcodeId : rdetail.barcodeId;
        currentBarcode = this.$store.getters.barcodes.find(
          (barcode) => barcode.barcodeId === barcodeId
        );
        if (!currentBarcode) {
          console.log('現在のバーコードが不明');
          return;
        }
      } else {
        console.log('端末登録時のバーコード制御');
      }

      if (currentBarcode && currentBarcode.barcodeType === codeType.KEYBOARD) {
        // キーボード入力の場合はテキスト入力モード
        this.codeType = codeType.KEYBOARD;
        this.isReadonlyBarcode = false;
      } else {
        // キーボード入力以外の場合はカメラ起動モード
        this.isReadonlyBarcode = true;
        await this.scanProc();
      }
    },
    /**
     * スキャンモーダル表示後
     */
    onShownScanModal: function () {
      // スキャンデータにフォーカスをセット
      if (!this.isReadonlyBarcode) this.$nextTick(() => this.$refs.modalData?.focus());
    },
    /**
     * スキャンモーダル非表示
     */
    onHiddenScanModal: function () {
      if (this.stream != null) {
        this.stream.getTracks().forEach((track) => track.stop());
        this.stream = null;
      }
    },
    /**
     * スキャンリトライ
     */
    onRetryScan: async function () {
      this.codeType = null;
      this.barcodeBuff = '';
      if (this.isReadonlyBarcode) await this.scanProc();
      else this.$nextTick(() => this.$refs.modalData?.focus());
    },
    /**
     * スキャンモーダル時スキャン処理
     */
    scanProc: async function () {
      // 背面カメラへアクセスする
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { exact: 'environment' }
        }
      });

      // カメラをvideo要素と結びつける
      const video = document.getElementById('scanVideo');
      video.srcObject = this.stream;

      // 再生を開始する
      await video.play();

      // 端末登録したコードに限定する
      let codeTypes = this.codeTypes;
      if (!codeTypes) codeTypes = [codeType.QR_CODE];
      console.log(`バーコード種類：${codeTypes}`);
      const detector = new window.BarcodeDetector({
        formats: codeTypes
      });

      // 一定時間ごと（ここでは500ミリ秒間隔）に
      // バーコードの検出を試みる
      const intervalId = setInterval(async () => {
        try {
          detector
            .detect(video)
            .then((barcodes) => {
              for (const code of barcodes) {
                // スキャン処理を停止する
                clearInterval(intervalId);
                video.pause();

                // バーコード内容を表示する
                console.log(code.rawValue);
                this.codeType = code.format;
                this.barcodeBuff = code.rawValue;
                break;
              }
            })
            .catch((error) => console.log(error));
        } catch (e) {
          console.log(e);
        }
      }, 500);
    },
    /**
     * スキャン決定(Android端末)
     */
    onDecideScan: function () {
      this.acceptScanResult({
        mCodeType: this.codeType,
        mStringData: this.barcodeBuff,
        mDecodeResult: scanResult.SUCCESS
      });
      this.barcodeBuff = '';
      this.$bvModal.hide('modal-scan');
    },
    /**
     * 入力モード変更
     */
    onChangeInputMode: function () {
      // 入力モードの切り替え(DX端末のみ)
      if (isDXTerminal()) {
        let ret = true;
        if (this.selectedInputMode === inputMode.BARCODE) {
          ret = scannerSetUseBarcode(this.codeTypes);
        } else if (this.selectedInputMode === inputMode.OCR) {
          ret = scannerSetUseOcr();
        } else {
          ret = scannerSetClear();
        }
        if (!ret) {
          this.$store.commit('setMessage', 'スキャナー利用コード種設定に失敗');
          return;
        }
      }

      // 子コンポーネントの入力モード変更処理呼出
      this.$refs.child.changeInputMode(this.selectedInputMode);
    },
    /**
     * ナビバー画面遷移
     * @param {string} pageName 画面ID
     */
    onNavigatePage: function (viewId) {
      // 指定画面IDへ
      this.$store.commit('clearHistoryCount');
      this.$router.replace({ path: `/${viewId}` });
    },
    /**
     * ナビバー履歴遷移
     */
    onNavigateBack: function () {
      // 前画面へ
      this.$store.commit('decrementHistoryCount');
      this.$router.back();
    },
    /**
     * ログアウト
     */
    logout: function () {
      // ログイン画面へ
      this.$router.replace({ path: '/UserLogin' });
    },
    /**
     * スキャン結果コールバック関数
     */
    acceptScanResult: function (result) {
      // スキャン結果をテキストへ反映する
      this.scanCodeType = result.mCodeType;
      this.scanData = result.mStringData;
      this.scanResult = result.mDecodeResult;

      // 読み取り結果の変更イベントを発生させる
      this.onChangeScan({ target: { value: this.scanResult } });
    },
    /**
     * スキャン内容変更
     */
    onChangeScan: async function () {
      // 読取情報、バーコード情報なしの場合はそのまま子コンポーネントのスキャンデータ解析後処理を呼出
      const reads = this.$store.getters.reads;
      const barcodes = this.$store.getters.barcodes;
      if (reads.length === 0 || barcodes.length === 0) {
        await this.$refs.child.analyzedScanData(this.scanData, [], null, allowDup.DUP_OK);
        return;
      }

      // 読取ヘッダが取得できない場合は処理しない
      const read = this.$store.getters.currentRead;
      if (!read) {
        console.log('読取ルール未選択のため中止');
        await this.$refs.child.analyzedScanData(this.scanData, [], null, allowDup.DUP_OK);
        return;
      }

      // 処理対象の読取詳細情報は現在の優先順以降
      // または、連続バーコードで次のバーコードID
      const nowPriorityNo = this.nowPriorityNo;
      let nextBarcodeId = this.nextBarcodeId;
      let readDetails = [];
      if (this.nextBarcodeId) {
        const readDetail = read.details.find((rdetail) => (rdetail.priorityNo = nowPriorityNo));
        readDetails.push(structuredClone(readDetail));
        readDetails[0].barcodeId = nextBarcodeId;
      } else {
        readDetails = read.details.filter((rdetail) => rdetail.priorityNo > nowPriorityNo);
      }
      if (readDetails.length === 0) {
        // 子コンポーネントのスキャンデータ解析後処理を呼出(エラー終了想定)
        console.log('次の優先順位対象がないため中止');
        await this.$refs.child.analyzedScanData({}, [], null, allowDup.DUP_OK);
        this.nowPriorityNo = 0;
        return;
      }

      for (const rdetail of readDetails) {
        // 現在の優先順位を保持する
        this.nowPriorityNo = rdetail.priorityNo;
        console.log(`読取明細マスタ処理開始 優先順位(${this.nowPriorityNo})`);

        // 照合対象スキャンデータであるかチェック
        const barcode = this.checkMatchedScanDataBarcode(
          this.scanCodeType,
          this.scanData,
          rdetail.barcodeId,
          barcodes,
          this.$store.getters.converts,
          this.headerValue,
          this.detailValues
        );

        // 不一致バーコードの場合は次の優先順へ
        if (!barcode) {
          console.log('バーコードが一致しないため次の優先順位へ');
          continue;
        }

        // 次のバーコードIDをセット
        this.nextBarcodeId = barcode.nextBarcodeId;

        // 子コンポーネントのスキャンデータ解析後処理を呼出
        if (!this.nextBarcodeId) {
          console.log(`バーコード決定 優先順位(${rdetail.priorityNo})`);
          const ret = await this.$refs.child.analyzedScanData(
            this.headerValue,
            this.detailValues,
            barcode,
            rdetail.dupType
          );
          this.nowPriorityNo = 0;
        } else {
          const nextBarcode = barcodes.find((barcode) => barcode.barcodeId === this.nextBarcodeId);
          this.$store.commit('setMessage', `続き(${nextBarcode.barcodeNm})を読取します`);
          console.log(`次の連続バーコード読取待ち バーコードID(${this.nextBarcodeId})`);
        }
        return;
      }

      // 該当バーコード無しのため子コンポーネントのスキャンデータ解析後処理を呼出(エラー終了想定)
      console.log('全ての優先順位で該当なしのため中止');
      await this.$refs.child.analyzedScanData({}, [], null, allowDup.DUP_OK);
      this.nowPriorityNo = 0;
    },
    /**
     * 画面初期化
     * @param {string} config 設定{viewId,useToolButtons,defaultInputMode,viewId,usableInputModeBits}
     */
    onInitView: function (config) {
      this.viewId = config['viewId'];
      const term = this.$store.getters.term;
      if (term) {
        // 画面タイトルをセット
        const func = term.funcs.find(function (x) {
          return x.formId === config['viewId'];
        });
        this.title = func.funcNm;
      } else {
        this.title = '端末登録';
      }

      this.useToolButtons = config['useToolButtons'];
      this.selectedInputMode = config['defaultInputMode'];
      this.$store.commit('setMessage', null);
      this.barcodeBuff = '';
      this.nextBarcodeId = null;

      this.inputModes = [];
      if (this.useToolButtons) {
        // バーコード
        if (config['usableInputModeBits'] & inputMode.BARCODE)
          this.inputModes.push({
            html: '<i class="bi bi-upc-scan h2"></i>',
            value: inputMode.BARCODE
          });
        // OCR
        if (config['usableInputModeBits'] & inputMode.OCR && isDXTerminal())
          this.inputModes.push({
            html: '<i class="bi bi-camera-fill h2"></i>',
            value: inputMode.OCR
          });
        // 手書き
        if (config['usableInputModeBits'] & inputMode.KEY)
          this.inputModes.push({
            html: '<i class="bi bi-pen-fill h2"></i>',
            value: inputMode.KEY
          });

        // 入力モード変更処理を呼び出す
        this.onChangeInputMode();

        this.enabledScan = false;
      }
    },
    /**
     * スキャン可否
     * @param {bool} value
     */
    onEnabledScan: function (value) {
      this.enabledScan = value;
    },
    /**
     * コード種変更
     * @param {string[]} コード種
     */
    onUpdateCodeTypes: function (value) {
      this.codeTypes = value;
    },
    /**
     * スキャンデータが照合対象かチェック
     * @param {string} scanCodeType スキャンコード種
     * @param {string} scanData スキャン結果
     * @param {number} barcodeId バーコードID
     * @param {object} barcodes バーコード情報配列
     * @param {object} converts 変換情報配列
     * @param {object} headerValue ヘッダ値(連想配列)を返す ※初期化済みを渡す
     * @param {object} detailValues 明細値(連想配列)の配列を返す(複数明細あり) ※配列初期化済みを渡す
     * @returns   連続バーコードの次のバーコード情報
     */
    checkMatchedScanDataBarcode: function (
      scanCodeType,
      scanData,
      barcodeId,
      barcodes,
      converts,
      headerValue,
      detailValues
    ) {
      console.log(`バーコード定義チェック開始 バーコードID(${barcodeId})`);

      // 対象バーコードが見つからない場合は対象外
      const barcode = barcodes.find(function (x) {
        return x.barcodeId === barcodeId;
      });
      if (!barcode) {
        console.log(`バーコードマスタに未登録のため対象外 バーコードID(${barcodeId})`);
        return null;
      }

      // バーコード種が異なる場合は対象外(キーボード入力は可)
      if (scanCodeType !== codeType.KEYBOARD && barcode.barcodeType !== scanCodeType) {
        console.log(`バーコード種が異なるため対象外 バーコード種(${scanCodeType})`);
        return null;
      }

      // バーコード長が指定された場合は長さチェック
      const scanLength = length_sjis(scanData);
      if (barcode.length && scanLength !== barcode.length) {
        console.log(`バーコード長が不一致のため対象外 バーコード長(${scanLength})`);
        return null;
      }

      // データ区切りが「固定長」「文字区切り」以外の場合は対象外
      if (barcode.dataSplit !== dataSplit.FIXED && barcode.dataSplit !== dataSplit.VARIABLE) {
        console.log(`データ区切りが想定外のため対象外 データ区切り(${barcode.dataSplit})`);
        return null;
      }

      // バーコードヘッダ/明細の件数取得
      let textValues = [];
      let detailCount = 0;
      const headerSize = barcode.details.filter(function (x) {
        return x.dataLayout === dataLayout.HEADER;
      }).length;
      const detailSize = barcode.details.filter(function (x) {
        return x.dataLayout === dataLayout.DETAIL;
      }).length;

      if (barcode.dataSplit === dataSplit.FIXED) {
        console.log('固定長バーコード切り出し開始');

        if (detailSize > 0) detailCount = (barcode.details.length - headerSize) / detailSize;
        for (const bdetail of barcode.details) {
          // フィールド値を取得
          let textValue = '';
          if (bdetail.columnStartPos == null || bdetail.columnLength == null) textValue = scanData;
          else textValue = substring_sjis(scanData, bdetail.columnStartPos, bdetail.columnLength);

          // 日付の場合は書式解除した値を追加
          if (bdetail.columnType === columnType.DATE)
            textValues.push(getDateValue(textValue, bdetail.dateFormat));
          else textValues.push(textValue);
        }
      } else {
        console.log('可変長バーコード切り出し開始');

        // テキストデータの全行を処理する
        let isFirstRow = true;
        let scanRows = scanData.split(str.NEW_LINE);
        detailCount = scanRows.length;
        for (const scanRow of scanRows) {
          let scanValues = scanRow.split(barcode.delimiter);

          for (const bdetail of barcode.details) {
            // ヘッダ情報で2行目以降は処理しない
            if (bdetail.dataLayout === dataLayout.HEADER && !isFirstRow) continue;

            // フィールド値を取得
            let textValue = '';
            if (!bdetail.columnJoin && bdetail.columnNo <= scanValues.length + 1)
              textValue = scanValues[bdetail.columnNo - 1];

            // 日付の場合は書式解除した値を追加
            if (bdetail.columnType === columnType.DATE)
              textValues.push(getDateValue(textValue, bdetail.dateFormat));
            else textValues.push(textValue);
          }
          isFirstRow = false;
        }
      }

      // ヘッダ情報をセット
      for (let i = 0; i < headerSize; i++) {
        const bdetail = barcode.details[i];
        let textValue = '';
        if (!bdetail.columnJoin) textValue = textValues[i];
        else textValue = this.getColumnJoinText(textValues, bdetail);

        headerValue[bdetail.columnId] = replaceConvertValue(textValue, bdetail.convertId, converts);
      }

      // 明細値をセット
      let bdetailIndex = headerSize;
      for (let i = 0; i < detailCount; i++) {
        if (detailValues.length < i + 1) detailValues.push({});
        const detailValue = detailValues[i];
        for (let j = 0; j < detailSize; j++) {
          const bdetail = barcode.details[j];
          let textValue = '';
          if (!bdetail.columnJoin) textValue = textValues[bdetailIndex];
          else textValue = this.getColumnJoinText(textValues, bdetail);

          detailValue[bdetail.columnId] = replaceConvertValue(
            textValue,
            bdetail.convertId,
            converts
          );
          bdetailIndex++;
        }
      }

      return barcode;
    },
    /**
     * 照合値変換
     * @param {string} textValue 元値
     * @param {!object} bdetail バーコード明細情報
     * @returns {string} 変換値、値無しは空文字、null:変換エラー
     */
    convertCompareValue: function (textValue, bdetail) {
      // NULL値の場合は空文字列を返す
      if (textValue === undefined || textValue === null || textValue === '') return '';

      let dateValue;
      switch (bdetail.columnType) {
        case columnType.TEXT: // 文字列
          // 変換情報から変換した値を返す
          return replaceConvertValue(textValue, bdetail.convertId, this.$store.getters.converts);
        case columnType.NUMBER: // 数値
          // 数値でない場合はエラーとする
          if (isNaN(textValue)) return null;

          // 数値変換して返す
          return Number(textValue).toString();
        case columnType.DATE: // 日付
          // 日付型値を取得する
          dateValue = getDateValue(textValue, bdetail.dateFormat);
          return dateValue;
        default:
          return null;
      }
    },
    /**
     * 結合値取得
     * @param {array} textValues 元値配列
     * @param {!object} bdetail バーコード明細情報
     * @returns {string} 結合値、値無しは空文字、null:結合エラー
     */
    getColumnJoinText: function (textValues, bdetail) {
      let value = null;
      const columnIds = bdetail.columnJoin.split('+');
      value = '';
      for (const columnId of columnIds) {
        // 列値が見つからない場合は異常
        if (!(columnId in textValues)) {
          return null;
        }
        value += textValues[columnId];
      }
      return value.trimEnd();
    },
    getHeaderLength: function (scanData, barcode) {
      // バーコード明細情報をヘッダ/明細に分割
      const barcodeHeaders = barcode.details.filter(function (x) {
        return x.dataLayout === dataLayout.HEADER;
      });
      const barcodeDetails = barcode.details.filter(function (x) {
        return x.dataLayout === dataLayout.DETAIL;
      });

      // ヘッダなしバーコードの場合は0を返す
      if (barcodeHeaders.length === 0) return 0;

      // 明細なしバーコードの場合はスキャン長を返す
      if (barcodeDetails.length === 0) return length_sjis(scanData);

      // 明細ありバーコードの場合は結合項目以外の最後の項目の開始位置＋長さを返す
      let length = 0;
      for (let i = barcodeHeaders.length - 1; i >= 0; i--) {
        if (!barcodeHeaders[i].columnJoin) {
          length = barcodeHeaders[i].columnStartPos + barcodeHeaders[i].columnLength - 1;
          break;
        }
      }

      return length;
    },
    getDetailLength: function (scanData, barcode) {
      // バーコード明細情報をヘッダ/明細に分割
      const barcodeHeaders = barcode.details.filter(function (x) {
        return x.dataLayout === dataLayout.HEADER;
      });
      const barcodeDetails = barcode.details.filter(function (x) {
        return x.dataLayout === dataLayout.DETAIL;
      });

      // 明細なしバーコードの場合は0を返す
      if (barcodeDetails.length === 0) return 0;

      // ヘッダなしバーコードの場合はスキャン長を返す
      if (barcodeHeaders.length === 0) return length_sjis(scanData);

      // ヘッダありバーコードの場合は結合項目以外の最後の項目の開始位置＋長さを返す
      let length = 0;
      for (let i = barcodeDetails.length - 1; i >= 0; i--) {
        if (!barcodeDetails[i].columnJoin) {
          length = barcodeDetails[i].columnStartPos + barcodeDetails[i].columnLength - 1;
          break;
        }
      }

      return length;
    }
  }
};
</script>

<style>
div#app {
  height: 100%;
}
div#functionArea {
  height: 100%;
}
header.b-sidebar-header {
  padding-top: 1rem !important;
  padding-bottom: 0.9rem !important;
}
</style>
